import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Bot, Sparkles, Users, Settings as SettingsIcon, Palette, PieChart, Database, GitBranch, Activity, User, Shield } from 'lucide-react';
import { Toaster } from 'react-hot-toast';
import ColorSettings from './components/ColorSettings';
import CustomBuildContent from './components/CustomBuildContent';
import UserManagement from './components/UserManagement';
import CustomerGroupManagement from '../customerGroupManagement/CustomerGroupManagement';
import CustomerSegmentation from '../segmentation/Segmentation';
import MetadataExtraction from '../metadataExtraction/MetadataExtraction';
import AgentWorkflow from '../AgentWorkflow/AgentWorkflow';
import ProcessTracker from '../processTracker/ProcessTracker';
import UserPreferencesSettings from './components/UserPreferencesSettings';
import GuardrailRegression from '../regression/Regression';

/**
 * Main Settings Screen component
 * @returns {React.ReactElement} - The settings screen component
 */
const Settings = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('colors');

  const handleBackToHome = () => {
    navigate('/home');
  };

  // Render the active tab content
  const renderTabContent = () => {
    switch (activeTab) {
      case 'colors':
        return <ColorSettings />;
      case 'customBuild':
        return <CustomBuildContent />;
      case 'userManagement':
        return <UserManagement />;
      case 'poolManagement':
        return <CustomerGroupManagement />;
      case 'clustering':
        return <CustomerSegmentation />;
      case 'metadata':
        return <MetadataExtraction />;
      case 'agentLineage':
        return <AgentWorkflow />;
      case 'processTracker':
        return <ProcessTracker />;
      case 'userPreferences':
        return <UserPreferencesSettings />;
      case 'guardrailRegression':
        return <GuardrailRegression />;
      default:
        return <div className="p-6 text-center">Select a setting to configure</div>;
    }
  };

  return (
    <div className="min-h-screen min-h-[100dvh] bg-gray-50">
      {/* Toast notifications */}
      <Toaster
        position="bottom-right"
        toastOptions={{
          duration: 3000,
          style: {
            background: '#333',
            color: '#fff',
          },
          success: {
            style: {
              background: 'green',
            },
          },
        }}
      />

      <div className="flex">
        <div className="w-64 fixed left-0 h-[100vh] h-[100dvh] bg-white border-r border-gray-200 overflow-y-auto">
          <nav className="p-4 h-full">
            <div className='flex items-center mb-4'>
              <button
                onClick={handleBackToHome}
                className="flex items-center text-primary transition-colors pr-3 rounded-md hover:bg-gray-100"
              >
                <ChevronLeft size={20} />
              </button>
              <h3 className="text-lg font-semibold text-primary">Settings</h3>
            </div>
            <ul className="space-y-2">
              <li>
                <button
                  onClick={() => setActiveTab('colors')}
                  className={`w-full flex items-center gap-2 p-3 rounded-lg transition-all duration-200 text-left ${activeTab === 'colors'
                    ? 'bg-primary text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                >
                  <Palette className="w-4 h-4" />
                  <span className="font-medium">Colors</span>
                </button>
              </li>
              {/* New User Preferences Tab */}
              <li>
                <button
                  onClick={() => setActiveTab('userPreferences')}
                  className={`w-full flex items-center gap-2 p-3 rounded-lg transition-all duration-200 text-left ${activeTab === 'userPreferences'
                    ? 'bg-primary text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                >
                  <User className="w-4 h-4" />
                  <span className="font-medium">User Preferences</span>
                </button>
              </li>
              <li>
                <button
                  onClick={() => setActiveTab('customBuild')}
                  className={`w-full flex items-center gap-2 p-3 rounded-lg transition-all duration-200 text-left ${activeTab === 'customBuild'
                    ? 'bg-primary text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                >
                  <Bot className="w-4 h-4" />
                  <span className="font-medium">Custom Build</span>
                </button>
              </li>
              <li>
                <button
                  onClick={() => setActiveTab('userManagement')}
                  className={`w-full flex items-center gap-2 p-3 rounded-lg transition-all duration-200 text-left ${activeTab === 'userManagement'
                    ? 'bg-primary text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                >
                  <Users className="w-4 h-4" />
                  <span className="font-medium">User Management</span>
                </button>
              </li>
              <li>
                <button
                  onClick={() => setActiveTab('poolManagement')}
                  className={`w-full flex items-center gap-2 p-3 rounded-lg transition-all duration-200 text-left ${activeTab === 'poolManagement'
                    ? 'bg-primary text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                >
                  <Users className="w-4 h-4" />
                  <span className="font-medium">Customer & Pool Management</span>
                </button>
              </li>
              <li>
                <button
                  onClick={() => setActiveTab('clustering')}
                  className={`w-full flex items-center gap-2 p-3 rounded-lg transition-all duration-200 text-left ${activeTab === 'clustering'
                    ? 'bg-primary text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                >
                  <PieChart className="w-4 h-4" />
                  <span className="font-medium">Clustering</span>
                </button>
              </li>
              <li>
                <button
                  onClick={() => setActiveTab('metadata')}
                  className={`w-full flex items-center gap-2 p-3 rounded-lg transition-all duration-200 text-left ${activeTab === 'metadata'
                    ? 'bg-primary text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                >
                  <Database className="w-4 h-4" />
                  <span className="font-medium">Neo4j to SQL Metadata Extraction</span>
                </button>
              </li>
              <li>
                <button
                  onClick={() => setActiveTab('agentLineage')}
                  className={`w-full flex items-center gap-2 p-3 rounded-lg transition-all duration-200 text-left ${activeTab === 'agentLineage'
                    ? 'bg-primary text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                >
                  <GitBranch className="w-4 h-4" />
                  <span className="font-medium">Agent Lineage</span>
                </button>
              </li>
              <li>
                <button
                  onClick={() => setActiveTab('guardrailRegression')}
                  className={`w-full flex items-center gap-2 p-3 rounded-lg transition-all duration-200 text-left ${activeTab === 'guardrailRegression'
                    ? 'bg-primary text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                >
                  <Shield className="w-4 h-4" />
                  <span className="font-medium">Model Evaluation</span>
                </button>
              </li>
              <li>
                <button
                  onClick={() => setActiveTab('processTracker')}
                  className={`w-full flex items-center gap-2 p-3 rounded-lg transition-all duration-200 text-left ${activeTab === 'processTracker'
                    ? 'bg-primary text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                >
                  <Activity className="w-4 h-4" />
                  <span className="font-medium">Process Tracker</span>
                </button>
              </li>

            </ul>
          </nav>
        </div>

        {/* Main content area - with margin to account for fixed sidebar */}
        <main className="flex-1 ml-64">
          {renderTabContent()}
        </main>
      </div>
    </div>
  );
};

export default Settings;